/**
 * Toast — Temporäre Benachrichtigung (Success/Error/Warning/Info) mit Auto-Dismiss.
 *
 * 23.10.2026: Warnung bekommt eigene Farbe (Orange) und Warn-Symbol; Erfolg
 * und Hinweis 3 s, Warnung und Fehler 5 s; lange Texte brechen um und
 * bleiben mit 16 px Rand auf dem Bildschirm. Jede neue Meldung bekommt
 * ihre volle Zeit — dafür setzt `AppShell` `key={toast.id}`.
 *
 * @module components/ui/Toast
 */

import { useEffect } from 'react';
import Icon from './Icon.jsx';
import { S } from '../../config/theme.js';

import type { ToastType } from '../../types';

interface ToastProps {
  message: string;
  type?: ToastType;
  onDone: () => void;
}

/** Anzeigedauer in ms: Warnung und Fehler länger, weil meist länger und wichtiger. */
const DAUER_KURZ = 3000;
const DAUER_LANG = 5000;

/**
 * Zeigt eine Meldung unten mittig und blendet sie nach 3 s (Erfolg/Hinweis)
 * bzw. 5 s (Warnung/Fehler) aus.
 *
 * @param props.message - Meldungstext (darf umbrechen)
 * @param props.type    - Art der Meldung, bestimmt Farbe, Symbol und Dauer
 * @param props.onDone  - wird nach Ablauf der Anzeigedauer aufgerufen
 */
export function Toast({ message, type, onDone }: ToastProps): React.JSX.Element {
  useEffect(() => {
    const t = setTimeout(onDone, type === 'warning' || type === 'error' ? DAUER_LANG : DAUER_KURZ);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bg =
    type === 'error'
      ? 'linear-gradient(135deg,#dc2626,#ef4444)'
      : type === 'warning'
        ? 'linear-gradient(135deg,#c2410c,#f59e0b)'
        : 'linear-gradient(135deg,#15803d,#22c55e)';
  const icon =
    type === 'success'
      ? <Icon name="check" size={14} color="#fff" />
      : type === 'error'
        ? <Icon name="cross-circle" size={14} color="#fff" />
        : type === 'warning'
          ? <Icon name="warning" size={14} color="#fff" />
          : '\u2139';

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        ...S.row,
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        background: bg,
        color: '#fff',
        padding: '12px 24px',
        borderRadius: 14,
        fontSize: 14,
        fontWeight: 600,
        boxShadow: '0 8px 32px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.15)',
        zIndex: 3000,
        animation: 'toastIn .35s cubic-bezier(.2,0,.1,1)',
        whiteSpace: 'normal',
        width: 'max-content',
        maxWidth: 'calc(100vw - 32px)',
        boxSizing: 'border-box',
        lineHeight: 1.35,
        backdropFilter: 'blur(8px)',
        letterSpacing: '-.2px',
        gap: 8,
      }}>
      <span style={{ flexShrink: 0, display: 'inline-flex' }}>{icon}</span>
      <span>{message}</span>
    </div>
  );
}
